import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { GripVertical, Info, Pencil, Trash2 } from "lucide-react";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { ConfirmDialog, InlineRenameInput } from "@web/components/ConfirmDialog.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { QueryErrorState } from "@web/features/diagnostics/QueryErrorState.tsx";
import {
  useCreateGenre,
  useDeleteGenre,
  useGenresFlat,
  useMergeGenre,
  useMoveGenre,
  useRenameGenre,
  type GenreListItem,
} from "@web/features/library/api.ts";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { nextDefaultName } from "@web/lib/names.ts";
import { cn } from "@web/lib/utils.ts";

const ROOT_DROP_ID = "drop:root";
const MAX_DEPTH = 8;

type GenreTreeNode = GenreListItem & { children: GenreTreeNode[] };

type DragGenre = { id: string; name: string; parentId: string | null };

type DropIntent =
  | { kind: "root" }
  | { kind: "into"; parentId: string }
  | { kind: "merge"; intoId: string }
  | { kind: "before"; parentId: string | null; beforeId: string }
  | { kind: "after"; parentId: string | null; afterId: string };

/** Updated during collision detection — authoritative cursor Y while dragging. */
let lastPointerY: number | null = null;

const genreCollision: CollisionDetection = (args) => {
  if (args.pointerCoordinates) {
    lastPointerY = args.pointerCoordinates.y;
  }
  const hits = pointerWithin(args);
  return hits.length > 0 ? hits : closestCenter(args);
};

function genreDragId(id: string) {
  return `drag:genre:${id}`;
}

function genreDropId(id: string) {
  return `drop:genre:${id}`;
}

function buildTree(items: GenreListItem[]): GenreTreeNode[] {
  const nodes = new Map<string, GenreTreeNode>();
  for (const g of items) {
    nodes.set(g.id, { ...g, children: [] });
  }
  const roots: GenreTreeNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parent_id === null ? null : nodes.get(node.parent_id);
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const byPos = (a: GenreTreeNode, b: GenreTreeNode) =>
    a.position - b.position || a.name.localeCompare(b.name);
  const sortWalk = (list: GenreTreeNode[]) => {
    list.sort(byPos);
    for (const n of list) sortWalk(n.children);
  };
  sortWalk(roots);
  return roots;
}

function flattenTree(roots: GenreTreeNode[]): { node: GenreTreeNode; depth: number }[] {
  const out: { node: GenreTreeNode; depth: number }[] = [];
  const walk = (nodes: GenreTreeNode[], depth: number) => {
    for (const n of nodes) {
      out.push({ node: n, depth });
      walk(n.children, depth + 1);
    }
  };
  walk(roots, 0);
  return out;
}

function findNode(roots: GenreTreeNode[], id: string): GenreTreeNode | null {
  for (const n of roots) {
    if (n.id === id) return n;
    const hit = findNode(n.children, id);
    if (hit) return hit;
  }
  return null;
}

function descendantIds(node: GenreTreeNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: GenreTreeNode) => {
    for (const child of n.children) {
      ids.add(child.id);
      walk(child);
    }
  };
  walk(node);
  return ids;
}

function nestPad(level: number): React.CSSProperties | undefined {
  if (level <= 0) return undefined;
  return { paddingLeft: `calc(var(--spacing) * ${level * 5})` };
}

/** Thin top/bottom strips reorder siblings; most of the row means nest into. */
const EDGE_PX = 7;

function clientYFromActivator(event: Event): number | null {
  if (event instanceof PointerEvent || event instanceof MouseEvent) return event.clientY;
  if (event instanceof TouchEvent) {
    const t = event.touches[0] ?? event.changedTouches[0];
    return t?.clientY ?? null;
  }
  return null;
}

function resolveIntent(
  overId: string | number,
  pointerY: number,
  overTop: number,
  overHeight: number,
  target: GenreListItem | undefined,
  mergeMode = false,
): DropIntent | null {
  if (overId === ROOT_DROP_ID) return { kind: "root" };
  if (!target) return null;
  if (overHeight <= 0) {
    return mergeMode
      ? { kind: "merge", intoId: target.id }
      : { kind: "into", parentId: target.id };
  }

  // Prefer fixed-pixel edges so short rows still leave a wide "nest into" band.
  const edge = Math.min(EDGE_PX, overHeight * 0.25);
  const y = pointerY - overTop;
  if (y < edge) {
    return { kind: "before", parentId: target.parent_id, beforeId: target.id };
  }
  if (y > overHeight - edge) {
    return { kind: "after", parentId: target.parent_id, afterId: target.id };
  }
  return mergeMode
    ? { kind: "merge", intoId: target.id }
    : { kind: "into", parentId: target.id };
}

function GenresHeader({
  loading,
  count,
  dragging,
  createPending,
  onAdd,
}: {
  loading: boolean;
  count: number;
  dragging: boolean;
  createPending: boolean;
  onAdd: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: ROOT_DROP_ID,
    disabled: !dragging,
  });

  return (
    <header
      ref={setNodeRef}
      className={cn(
        "flex shrink-0 items-end justify-between gap-4 border-b border-line px-5 py-4 transition-colors",
        dragging && "ring-1 ring-inset ring-line",
        isOver && "bg-row-selected ring-1 ring-inset ring-accent",
      )}
    >
      <div className="min-w-0">
        <p className="text-xs font-semibold tracking-wider text-faint uppercase">Browse</p>
        <h1 className="text-2xl font-semibold tracking-tight">Genres</h1>
        <p className="mt-1 text-sm text-muted">
          {loading
            ? "Loading…"
            : dragging
              ? isOver
                ? "Drop to move to top-level"
                : "Drop onto a genre to nest · Shift+drop to merge"
              : `${count.toLocaleString()} genres · drag to nest · Shift+drop merges · click details`}
        </p>
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={createPending || dragging}
        onClick={onAdd}
      >
        Add genre
      </Button>
    </header>
  );
}

function GenreRow({
  genre,
  depth,
  editing,
  draft,
  canDelete,
  dropHint,
  onDraftChange,
  onCommitRename,
  onCancelRename,
  onStartRename,
  onDelete,
  onActivate,
  onDoubleActivate,
  onOpenDetail,
}: {
  genre: GenreListItem;
  depth: number;
  editing: boolean;
  draft: string;
  canDelete: boolean;
  dropHint: "into" | "before" | "after" | "merge" | null;
  onDraftChange: (v: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onStartRename: () => void;
  onDelete: () => void;
  onActivate: () => void;
  onDoubleActivate: () => void;
  onOpenDetail: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: genreDragId(genre.id),
    data: {
      id: genre.id,
      name: genre.name,
      parentId: genre.parent_id,
    } satisfies DragGenre,
    disabled: editing,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: genreDropId(genre.id),
    data: { id: genre.id },
  });

  const setRefs = useCallback(
    (node: HTMLLIElement | null) => {
      setDragRef(node);
      setDropRef(node);
    },
    [setDragRef, setDropRef],
  );

  return (
    <li
      ref={setRefs}
      data-genre-drop={genre.id}
      className={cn(
        "group relative flex items-center pr-1",
        isDragging && "opacity-40",
        !editing && "hover:bg-row-hover",
        isOver && dropHint === "into" && "bg-row-selected ring-1 ring-accent",
        isOver && dropHint === "merge" && "bg-accent/15 ring-2 ring-accent",
      )}
      style={nestPad(depth)}
    >
      {isOver && dropHint === "before" && (
        <span className="pointer-events-none absolute inset-x-2 top-0 z-10 h-0.5 bg-accent" />
      )}
      {isOver && dropHint === "after" && (
        <span className="pointer-events-none absolute inset-x-2 bottom-0 z-10 h-0.5 bg-accent" />
      )}
      {isOver && dropHint === "merge" && (
        <span className="pointer-events-none absolute inset-x-2 top-1/2 z-10 -translate-y-1/2 truncate px-2 text-center text-xs font-semibold text-accent">
          Merge into {genre.name}
        </span>
      )}

      {editing ? (
        <div className="flex min-w-0 flex-1 items-center gap-3 px-2 py-3">
          <span
            className="size-3 shrink-0 rounded-full bg-faint"
            style={genre.color ? { backgroundColor: genre.color } : undefined}
          />
          <InlineRenameInput
            value={draft}
            onChange={onDraftChange}
            onCommit={onCommitRename}
            onCancel={onCancelRename}
          />
        </div>
      ) : (
        <>
          <button
            type="button"
            className="flex size-8 shrink-0 cursor-grab items-center justify-center text-faint hover:text-muted active:cursor-grabbing"
            aria-label={`Drag ${genre.name}`}
            title="Drag to nest or reorder"
            {...listeners}
            {...attributes}
          >
            <GripVertical className="size-3.5" />
          </button>
          <div
            role="button"
            tabIndex={0}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 py-3 pr-2 text-left"
            title="Click for details · double-click to open tracks"
            onClick={onActivate}
            onDoubleClick={(e) => {
              e.preventDefault();
              onDoubleActivate();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onOpenDetail();
              }
            }}
          >
            <span
              className="size-3 shrink-0 rounded-full bg-faint"
              style={genre.color ? { backgroundColor: genre.color } : undefined}
            />
            <span className="min-w-0 flex-1 truncate font-medium">{genre.name}</span>
            <span className="font-mono text-xs text-faint tabular-nums">{genre.usage_count}</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Rename ${genre.name}`}
            title="Rename"
            className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
            onClick={onStartRename}
          >
            <Pencil className="size-3.5" />
          </Button>
          {canDelete && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Delete ${genre.name}`}
              title="Delete (unused)"
              className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              onClick={onDelete}
            >
              <Trash2 className="size-3.5" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Details for ${genre.name}`}
            title="Details"
            className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
            onClick={onOpenDetail}
          >
            <Info className="size-4" />
          </Button>
        </>
      )}
    </li>
  );
}

export function GenresPage() {
  const genres = useGenresFlat();
  const create = useCreateGenre();
  const rename = useRenameGenre();
  const remove = useDeleteGenre();
  const merge = useMergeGenre();
  const move = useMoveGenre();
  const openDetail = useOpenDetail();
  const navigate = useNavigate();
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerYRef = useRef(0);
  const pointerStartYRef = useRef<number | null>(null);
  const shiftHeldRef = useRef(false);
  const [editing, setEditing] = useState<{
    id: string;
    draft: string;
    original: string;
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<GenreListItem | null>(null);
  const [deleteMode, setDeleteMode] = useState<"promote" | "absorb" | "reassign_parent">(
    "reassign_parent",
  );
  const [activeDrag, setActiveDrag] = useState<DragGenre | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<"into" | "before" | "after" | "merge" | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const byId = useMemo(() => {
    const map = new Map<string, GenreListItem>();
    for (const g of genres.data ?? []) map.set(g.id, g);
    return map;
  }, [genres.data]);

  const tree = useMemo(() => buildTree(genres.data ?? []), [genres.data]);
  const flatRows = useMemo(() => flattenTree(tree), [tree]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      shiftHeldRef.current = e.shiftKey;
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  const startRename = useCallback((id: string, name: string) => {
    setEditing({ id, draft: name, original: name });
  }, []);

  const cancelRename = useCallback(() => setEditing(null), []);

  const commitRename = useCallback(() => {
    if (!editing) return;
    const next = editing.draft.trim();
    if (!next || next === editing.original) {
      setEditing(null);
      return;
    }
    rename.mutate({ id: editing.id, name: next });
    setEditing(null);
  }, [editing, rename]);

  function openGenreTracks(genre: GenreListItem) {
    void navigate(`/all?genre=${encodeURIComponent(genre.id)}`);
  }

  function onGenreActivate(genre: GenreListItem) {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      openDetail({ type: "genre", id: genre.id, label: genre.name });
    }, 250);
  }

  function onGenreDoubleActivate(genre: GenreListItem) {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    openGenreTracks(genre);
  }

  async function addGenre() {
    const name = nextDefaultName(
      "New genre",
      (genres.data ?? []).map((g) => g.name),
    );
    const created = await create.mutateAsync(name);
    setEditing({ id: created.id, draft: created.name, original: created.name });
  }

  function confirmDelete() {
    if (!pendingDelete) return;
    const hasChildren = (genres.data ?? []).some((g) => g.parent_id === pendingDelete.id);
    const hasAssignments = pendingDelete.usage_count > 0;
    let mode: "reassign_parent" | "promote" | "absorb" | undefined;

    if (!hasChildren && !hasAssignments) {
      remove.mutate(
        { id: pendingDelete.id },
        { onSuccess: () => setPendingDelete(null) },
      );
      return;
    }

    if (!hasChildren && hasAssignments) {
      if (!pendingDelete.parent_id) {
        setPendingDelete(null);
        return;
      }
      mode = "reassign_parent";
    } else {
      mode = deleteMode === "absorb" ? "absorb" : "promote";
    }

    remove.mutate(
      { id: pendingDelete.id, mode },
      { onSuccess: () => setPendingDelete(null) },
    );
  }

  function depthOf(id: string | null): number {
    if (!id) return -1;
    let d = 0;
    let p: string | null = id;
    while (p) {
      d += 1;
      p = byId.get(p)?.parent_id ?? null;
      if (d > 50) break;
    }
    return d - 1;
  }

  function updateDropHint(nextOverId: string | null, pointerY: number) {
    setOverId(nextOverId);
    if (!nextOverId || nextOverId === ROOT_DROP_ID) {
      setDropHint(null);
      return;
    }
    const genreId = nextOverId.startsWith("drop:genre:")
      ? nextOverId.slice("drop:genre:".length)
      : null;
    const target = genreId ? byId.get(genreId) : undefined;
    if (!target) {
      setDropHint(null);
      return;
    }
    const el = document.querySelector(`[data-genre-drop="${genreId}"]`);
    // fallback: use over rect via relative — without element use into
    if (!(el instanceof HTMLElement)) {
      setDropHint("into");
      return;
    }
    const rect = el.getBoundingClientRect();
    const intent = resolveIntent(
      nextOverId,
      pointerY,
      rect.top,
      rect.height,
      target,
      shiftHeldRef.current,
    );
    if (!intent) {
      setDropHint(null);
      return;
    }
    if (intent.kind === "root") setDropHint(null);
    else if (intent.kind === "merge") setDropHint("merge");
    else setDropHint(intent.kind);
  }

  function onDragStart(event: DragStartEvent) {
    const data = event.active.data.current as DragGenre | undefined;
    if (data) setActiveDrag(data);
    pointerStartYRef.current = clientYFromActivator(event.activatorEvent);
    if (pointerStartYRef.current != null) {
      pointerYRef.current = pointerStartYRef.current;
      lastPointerY = pointerStartYRef.current;
    }
  }

  function onDragMove(event: DragMoveEvent) {
    // Prefer live pointer from collision detection; fall back to activator + delta.
    if (lastPointerY != null) {
      pointerYRef.current = lastPointerY;
    } else if (pointerStartYRef.current != null) {
      pointerYRef.current = pointerStartYRef.current + event.delta.y;
    }
    const nextOver =
      typeof event.over?.id === "string" ? event.over.id : event.over?.id != null
        ? String(event.over.id)
        : null;
    updateDropHint(nextOver, pointerYRef.current);
  }

  function onDragEnd(event: DragEndEvent) {
    const active = event.active.data.current as DragGenre | undefined;
    const nextOverId =
      typeof event.over?.id === "string" ? event.over.id : event.over?.id != null
        ? String(event.over.id)
        : null;
    // Final pointer sample — collision may have run after the last onDragMove.
    if (lastPointerY != null) pointerYRef.current = lastPointerY;
    setActiveDrag(null);
    setOverId(null);
    setDropHint(null);
    pointerStartYRef.current = null;
    lastPointerY = null;
    if (!active || nextOverId === null) return;

    const targetId = nextOverId.startsWith("drop:genre:")
      ? nextOverId.slice("drop:genre:".length)
      : null;
    const target = targetId ? byId.get(targetId) : undefined;
    let overTop = 0;
    let overHeight = 0;
    if (targetId) {
      const el = document.querySelector(`[data-genre-drop="${targetId}"]`);
      if (el instanceof HTMLElement) {
        const rect = el.getBoundingClientRect();
        overTop = rect.top;
        overHeight = rect.height;
      }
    }

    const intent = resolveIntent(
      nextOverId,
      pointerYRef.current,
      overTop,
      overHeight,
      target,
      shiftHeldRef.current,
    );
    if (!intent) return;

    const dragged = findNode(tree, active.id);
    if (!dragged) return;
    const banned = descendantIds(dragged);
    banned.add(active.id);

    if (intent.kind === "merge") {
      if (banned.has(intent.intoId) || intent.intoId === active.id) return;
      merge.mutate({ fromId: active.id, intoId: intent.intoId });
      return;
    }

    if (intent.kind === "root") {
      if (active.parentId === null) return;
      move.mutate({ id: active.id, parent_id: null });
      return;
    }

    if (intent.kind === "into") {
      if (banned.has(intent.parentId)) return;
      if (active.parentId === intent.parentId) return;
      if (depthOf(intent.parentId) + 1 >= MAX_DEPTH) return;
      move.mutate({ id: active.id, parent_id: intent.parentId });
      return;
    }

    if (intent.kind === "before") {
      if (intent.beforeId === active.id) return;
      if (intent.parentId !== null && banned.has(intent.parentId)) return;
      if (intent.parentId !== null && depthOf(intent.parentId) + 1 >= MAX_DEPTH) return;
      move.mutate({
        id: active.id,
        parent_id: intent.parentId,
        before_id: intent.beforeId,
      });
      return;
    }

    if (intent.kind === "after") {
      if (intent.afterId === active.id) return;
      if (intent.parentId !== null && banned.has(intent.parentId)) return;
      if (intent.parentId !== null && depthOf(intent.parentId) + 1 >= MAX_DEPTH) return;
      move.mutate({
        id: active.id,
        parent_id: intent.parentId,
        after_id: intent.afterId,
      });
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={genreCollision}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setActiveDrag(null);
        setOverId(null);
        setDropHint(null);
        pointerStartYRef.current = null;
        lastPointerY = null;
      }}
    >
      <div className="flex h-full flex-col">
        <GenresHeader
          loading={genres.isLoading}
          count={genres.data?.length ?? 0}
          dragging={activeDrag !== null}
          createPending={create.isPending}
          onAdd={() => void addGenre()}
        />

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {genres.isLoading && (
            <div className="space-y-2">
              {Array.from({ length: 12 }, (_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}

          {genres.isError && (
            <QueryErrorState
              error={genres.error}
              onRetry={() => void genres.refetch()}
              actionLabel="load genres"
            />
          )}

          {genres.isSuccess && genres.data.length === 0 && (
            <EmptyState
              title="No genres yet"
              description="Add one to start tagging tracks. Drag genres onto each other to nest them."
              action={
                <Button type="button" variant="secondary" onClick={() => void addGenre()}>
                  Add genre
                </Button>
              }
            />
          )}

          {genres.isSuccess && genres.data.length > 0 && (
            <ul className="divide-y divide-line">
              {flatRows.map(({ node, depth }) => {
                const isEditing = editing?.id === node.id;
                const rowOver = overId === genreDropId(node.id) ? dropHint : null;
                return (
                  <GenreRow
                    key={node.id}
                    genre={node}
                    depth={depth}
                    editing={isEditing}
                    draft={isEditing && editing ? editing.draft : node.name}
                    canDelete={true}
                    dropHint={rowOver}
                    onDraftChange={(draft) => editing && setEditing({ ...editing, draft })}
                    onCommitRename={commitRename}
                    onCancelRename={cancelRename}
                    onStartRename={() => startRename(node.id, node.name)}
                    onDelete={() => {
                      setPendingDelete(node);
                      setDeleteMode(
                        (genres.data ?? []).some((g) => g.parent_id === node.id)
                          ? "promote"
                          : "reassign_parent",
                      );
                    }}
                    onActivate={() => {
                      if (isEditing) return;
                      onGenreActivate(node);
                    }}
                    onDoubleActivate={() => {
                      if (isEditing) return;
                      onGenreDoubleActivate(node);
                    }}
                    onOpenDetail={() =>
                      openDetail({ type: "genre", id: node.id, label: node.name })
                    }
                  />
                );
              })}
            </ul>
          )}
        </div>

        <ConfirmDialog
          open={pendingDelete !== null}
          title={pendingDelete ? `Delete “${pendingDelete.name}”?` : "Delete genre?"}
          description={
            !pendingDelete
              ? ""
              : (genres.data ?? []).some((g) => g.parent_id === pendingDelete.id)
                ? "This genre has children. Choose how to keep assignments and structure."
                : pendingDelete.usage_count > 0
                  ? pendingDelete.parent_id
                    ? "Assignments move to the parent genre. Nothing is orphaned."
                    : `This root genre has ${pendingDelete.usage_count} assignment(s). Nest it under another genre first, or merge it.`
                  : "Child genres (if any) move up to its parent."
          }
          confirmLabel="Delete"
          busy={remove.isPending}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        >
          {pendingDelete &&
            (genres.data ?? []).some((g) => g.parent_id === pendingDelete.id) && (
              <div className="mt-3 space-y-2 text-sm text-muted">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="delete-mode"
                    checked={deleteMode === "promote"}
                    onChange={() => setDeleteMode("promote")}
                  />
                  Promote children to this genre’s level
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="delete-mode"
                    checked={deleteMode === "absorb"}
                    onChange={() => setDeleteMode("absorb")}
                  />
                  Absorb children into grandparent (assignments move up)
                </label>
              </div>
            )}
        </ConfirmDialog>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeDrag ? (
          <div className="rounded-md bg-panel-elevated px-3 py-2 text-sm font-medium shadow-md ring-1 ring-line">
            {activeDrag.name}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
